import { ConfigOption, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyNebularInputTypeComponent } from './formly-input.type';
import { FormlyNebularNumberTypeComponent } from './formly-number.type';
import { FormlyNebularCheckboxTypeComponent } from './formly-checkbox.type';
import { FormlyNebularSelectTypeComponent } from './formly-select.type';
import { FormlyNebularGroupTypeComponent } from './formly-group.type';
import { FormlyNebularArrayTypeComponent } from './formly-array.type';
import { FormlyNebularStringArrayTypeComponent } from './formly-string-array.type';
import { FormlyNebularFormFieldWrapperComponent } from './formly-form-field.wrapper';

/** Formly type/wrapper registration for the data-hub dynamic spec form. */
export const DATA_HUB_FORMLY_CONFIG: ConfigOption = {
	types: [
		{ name: 'string', component: FormlyNebularInputTypeComponent, wrappers: ['nebular-form-field'] },
		{ name: 'integer', component: FormlyNebularNumberTypeComponent, wrappers: ['nebular-form-field'] },
		{ name: 'number', component: FormlyNebularNumberTypeComponent, wrappers: ['nebular-form-field'] },
		{ name: 'boolean', component: FormlyNebularCheckboxTypeComponent, wrappers: ['nebular-form-field'] },
		{ name: 'enum', component: FormlyNebularSelectTypeComponent, wrappers: ['nebular-form-field'] },
		{ name: 'array', component: FormlyNebularArrayTypeComponent, wrappers: ['nebular-form-field'] },
		{ name: 'string-array', component: FormlyNebularStringArrayTypeComponent, wrappers: ['nebular-form-field'] },
		{ name: 'object', component: FormlyNebularGroupTypeComponent },
		{ name: 'multischema', component: FormlyNebularGroupTypeComponent },
		// overrides ngx-formly's own bare pass-through used for <formly-form>'s top-level `fields` array,
		// so the top-level fields get the same row/col-md-4 grid as any nested object field
		{ name: 'formly-group', component: FormlyNebularGroupTypeComponent }
	],
	wrappers: [{ name: 'nebular-form-field', component: FormlyNebularFormFieldWrapperComponent }]
};

/**
 * FormlyJsonschema's `map` hook: copies the standard JSON-Schema/OpenAPI keywords it doesn't
 * know about (format, example) from the source schema node onto the mapped field's props.
 * Feature-specific annotations (e.g. data-hub's `x-dateFormat`) don't belong here - compose
 * this with your own `map` function instead of extending it.
 */
export function mapSchemaToField(field: FormlyFieldConfig, schema: any): FormlyFieldConfig {
	field.props = field.props || {};
	if (schema.format) field.props['format'] = schema.format;
	if (schema.example !== undefined) field.props['example'] = schema.example;
	// String arrays render as a multi-value ng-select instead of the generic one-row-per-item
	// 'array' type (which stays reserved for arrays of objects/groups): a plain dropdown when
	// items are constrained to an enum, free-text tag entry otherwise.
	// FormlyJsonschema sets `fieldArray` on every array schema node to drive that per-item UI -
	// ngx-formly's builder skips creating a control at all when `fieldArray` is set (regardless
	// of `type`), so it has to be cleared here for the field to get a plain FormControl.
	if (schema.type === 'array' && schema.items?.type === 'string') {
		field.type = 'string-array';
		if (schema.items.enum) field.props['options'] = schema.items.enum;
		delete field.fieldArray;
		delete field.fieldGroup;
	}
	// Object nodes that don't earn a `<fieldset>` section of their own. FormlyJsonschema resolves
	// `$ref`s before calling this hook and maps children before their parent, so `field.fieldGroup`
	// is already the final list of rendered children by the time we get here.
	if (field.type === 'object' && field.key != null) {
		const children = field.fieldGroup ?? [];
		if (children.length === 0) {
			// A schema with no properties at all (e.g. an endpoint whose `request` takes no arguments)
			// has nothing to render, but the API still expects the key - defaulting it to `{}` keeps
			// `request: {}` in the submitted payload instead of dropping the property entirely.
			field.defaultValue = {};
		} else if (children.length === 1) {
			// A wrapper around a single property (e.g. `pubDate` -> PartialDate's lone `value`) is a
			// section header, a legend and a border around one input - render the child inline in the
			// parent's grid instead, under the wrapper's name rather than the inner property's.
			field.props['flatten'] = true;
			relabel(children[0], field.props['label'] || humanizeFieldKey(field.key));
		}
	}
	return field;
}

/** Retargets a flattened wrapper's label onto whichever descendant actually ends up rendered. */
function relabel(field: FormlyFieldConfig, label: string) {
	let target = field;
	while (target.props?.['flatten']) target = target.fieldGroup[0];
	target.props = target.props || {};
	target.props.label = label;
}

/** Turns a schema property key into a display label, e.g. `parserOptions` -> "Parser Options". */
export function humanizeFieldKey(key: FormlyFieldConfig['key']): string {
	const raw = Array.isArray(key) ? key[key.length - 1] : key;
	return String(raw ?? '')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/^./, c => c.toUpperCase());
}

/** Resolves a `$ref` against a components.schemas map, keeping any sibling keywords (e.g. nullable) that ride alongside it. */
export function resolveSchemaRef(schema: any, schemas: Record<string, any> | undefined): any {
	if (!schema?.$ref) return schema;
	const name = schema.$ref.split('/').pop();
	const resolved = schemas?.[name];
	return resolved ? { ...resolved, ...schema } : schema;
}

/**
 * Matches the pre-formly forms' (unintentional but desired) behavior: *required* single-select
 * enum dropdowns start out on their first option instead of blank, so schedule/pipeline creation
 * doesn't require touching every required enum field just to pick the obvious default - optional
 * enum fields (e.g. p_aeMode) are left blank as before. `field.props.required` isn't known yet at
 * map-hook time (formly-json-schema only sets it via an expression evaluated once the field is
 * rendered), so this walks the built field tree back against the raw schema's `required` arrays
 * instead. Only kicks in when the schema doesn't already specify a `default`, and only for new
 * records - editing an existing one loads real values, so defaultValue is never applied then.
 * Shared by data-hub and pipelines so the rule isn't reimplemented per consumer.
 */
export function applyRequiredEnumDefaults(
	fields: FormlyFieldConfig[],
	schema: any,
	schemas: Record<string, any> | undefined
) {
	const resolved = resolveSchemaRef(schema, schemas);
	if (!resolved?.properties) return;
	const required = new Set<string>(resolved.required ?? []);

	for (const field of fields ?? []) {
		const key = String(field.key);

		if (field.type === 'enum' && !field.props?.multiple && field.defaultValue === undefined && required.has(key)) {
			const firstOption = field.props?.options?.[0];
			if (firstOption) field.defaultValue = firstOption.value;
		}

		if (field.fieldGroup?.length) {
			applyRequiredEnumDefaults(field.fieldGroup, resolved.properties[key], schemas);
		}
	}
}
